import React, { useState } from 'react';
import { GripVertical, ArrowUp, ArrowDown, Lock, RotateCcw, ListOrdered, LayoutTemplate } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';

const THEMES = [['short', 'Short'], ['regular', 'Regular'], ['detailed', 'Detailed']];

const move = (list, from, to) => {
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

// Drag (or use the arrows) to set the order of sections in every quotation output.
export const SectionOrderCard = ({ catalog, layout, onChange }) => {
  const [dragIdx, setDragIdx] = useState(null);
  const label = Object.fromEntries(catalog.items.map((i) => [i.key, i.label]));
  const order = layout.order;
  const setOrder = (next) => onChange({ ...layout, order: next });
  return (
    <Card data-testid="section-order-card">
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2"><ListOrdered className="w-5 h-5" />Section Order</CardTitle>
          <CardDescription>Order of sections in the saved view, shared link, PDF, Word and email.</CardDescription>
        </div>
        <Button type="button" variant="outline" size="sm" className="h-11" onClick={() => setOrder(catalog.default_order)} data-testid="section-order-reset-btn">
          <RotateCcw className="w-4 h-4 mr-1" />Reset to Default
        </Button>
      </CardHeader>
      <CardContent>
        <ol className="space-y-1.5" data-testid="section-order-list">
          {order.map((key, i) => (
            <li key={key} draggable onDragStart={() => setDragIdx(i)} onDragOver={(e) => e.preventDefault()}
              onDrop={() => { if (dragIdx !== null && dragIdx !== i) setOrder(move(order, dragIdx, i)); setDragIdx(null); }}
              className={`flex items-center gap-2 border rounded-md bg-white px-2 ${dragIdx === i ? 'opacity-50' : ''}`}
              data-testid={`section-order-item-${key}`}>
              <GripVertical className="w-4 h-4 text-gray-400 cursor-grab shrink-0" aria-hidden="true" />
              <span className="text-xs text-gray-400 w-5">{i + 1}</span>
              <span className="flex-1 text-sm">{label[key]}</span>
              <Button type="button" variant="ghost" size="icon" className="h-11 w-11" disabled={i === 0} aria-label={`Move ${label[key]} up`}
                onClick={() => setOrder(move(order, i, i - 1))} data-testid={`section-order-up-${key}`}><ArrowUp className="w-4 h-4" /></Button>
              <Button type="button" variant="ghost" size="icon" className="h-11 w-11" disabled={i === order.length - 1} aria-label={`Move ${label[key]} down`}
                onClick={() => setOrder(move(order, i, i + 1))} data-testid={`section-order-down-${key}`}><ArrowDown className="w-4 h-4" /></Button>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
};

// Tick which sections each document style (Short / Regular / Detailed) prints.
export const ThemeStyleCard = ({ catalog, layout, onChange }) => {
  const toggle = (theme, key, on) => {
    const cur = new Set(layout.themes[theme]);
    if (on) cur.add(key); else cur.delete(key);
    onChange({ ...layout, themes: { ...layout.themes, [theme]: layout.order.filter((k) => cur.has(k)) } });
  };
  const items = layout.order.map((k) => catalog.items.find((i) => i.key === k)).filter(Boolean);
  return (
    <Card data-testid="theme-style-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><LayoutTemplate className="w-5 h-5" />Theme Style Configuration</CardTitle>
        <CardDescription>Choose the sections each style shows when a quotation is downloaded or shared. Locked sections always appear.</CardDescription>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="theme-style-table">
          <thead><tr className="border-b">
            <th className="text-left py-2 pr-3 font-semibold text-gray-600">Section</th>
            {THEMES.map(([t, l]) => <th key={t} className="py-2 px-3 font-semibold text-gray-600 text-center">{l}</th>)}
          </tr></thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.key} className="border-b last:border-0">
                <td className="py-1 pr-3">
                  <span className="inline-flex items-center gap-1.5">{it.label}{it.locked && <Lock className="w-3.5 h-3.5 text-gray-400" aria-label="Always on" />}</span>
                </td>
                {THEMES.map(([t]) => (
                  <td key={t} className="py-1 px-3 text-center">
                    <label className="inline-flex items-center justify-center min-w-[44px] min-h-[44px] cursor-pointer">
                      <Checkbox checked={it.locked || layout.themes[t].includes(it.key)} disabled={it.locked}
                        onCheckedChange={(c) => toggle(t, it.key, !!c)} data-testid={`theme-${t}-${it.key}`} aria-label={`${it.label} in ${t}`} />
                    </label>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
};
